import React, { useState, useEffect } from 'react';
import { Control, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { TrashIcon, LockClosedIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { formatDateSafe, createDateSafe } from '@/utils/dateUtils';
import { useCollaboratorSalary } from '@/features/Collaborators/hooks/data/useCollaboratorSalary';
import { useSalaryPermissions } from '@/features/Collaborators/hooks/useSalaryPermissions';
import { useSalaryMutation } from '@/features/Collaborators/hooks/data/useSalaryMutation';
import { SalaryHistoryEntryWithId } from '@/features/Collaborators/Models/salaryHistory.types';

interface SalaryHistoryFieldsetProps {
    context: CollaboratorFormContext;
    control: Control<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
    collaboratorId?: string;
}

const SalaryHistoryFieldset: React.FC<SalaryHistoryFieldsetProps> = ({
    context,
    control,
    setValue,
    watch,
    errors,
    isSubmitting = false,
    collaboratorId
}) => {
    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Permission hooks
    const {
        canViewSalary,
        canEditSalary,
        canDeleteHistory,
        shouldShowAsDisabled
    } = useSalaryPermissions();

    // Salary data hooks
    const {
        data: salaryData,
        history: salaryHistory,
        isLoading,
        isError
    } = useCollaboratorSalary(collaboratorId || null, {
        includeHistory: true,
        historyLimit: 20,
        enableRealTime: true
    });


    // Mutation hook for deleting history
    const { deleteHistory } = useSalaryMutation(collaboratorId || '');

    // Estado local para IDs removidos (só persiste no save)
    const [locallyRemovedIds, setLocallyRemovedIds] = useState<string[]>([]);

    // Garantir que salaryHistoryDeletedIds está inicializado
    useEffect(() => {
        const currentValue = watch('salaryHistoryDeletedIds');
        if (currentValue === undefined) {
            setValue('salaryHistoryDeletedIds', [], { shouldDirty: false });
        }
    }, []);

    // Early return se não deve mostrar histórico neste contexto
    if (!shouldShow('salaryHistoryShowHistory')) {
        return null;
    }

    // Watch para obter dados do formulário (para novos registros não salvos ainda)
    const formData = watch();
    const unsavedSalaries = formData.salaryHistorySalaries || [];

    // Handler para remover salário não salvo (do formulário)
    const handleRemoveUnsavedSalary = (index: number) => {
        if (!canEditSalary) return;
        const updatedSalaries = unsavedSalaries.filter((_: any, i: number) => i !== index);
        setValue('salaryHistorySalaries', updatedSalaries);
    };

    // Handler para remover salário do histórico (marca para remoção no save)
    const handleRemoveSavedSalary = (historyId: string) => {
        if (!canEditSalary) {
            return;
        }

        if (window.confirm('Tem certeza que deseja remover este registro do histórico?')) {
            // Adicionar à lista de removidos localmente
            const newLocallyRemovedIds = [...locallyRemovedIds, historyId];
            setLocallyRemovedIds(newLocallyRemovedIds);

            // Marcar para deletar no save
            const currentDeletedIds = formData.salaryHistoryDeletedIds || [];
            const newDeletedIds = currentDeletedIds.includes(historyId)
                ? currentDeletedIds
                : [...currentDeletedIds, historyId];

            setValue('salaryHistoryDeletedIds', newDeletedIds);
        }
    };

    // Verificar se pode remover salários
    // Agora ignora o flag allowRemove da config; basta permissão do usuário
    const canRemove = canDeleteHistory;

    // Se não tem permissão para ver salário, mostrar mensagem
    if (!canViewSalary) {
        return (
            <fieldset data-testid="salary-history-fieldset" className="border p-4 rounded-md bg-gray-50">
                <legend className="text-sm font-medium px-1 text-gray-400">Histórico Salarial</legend>
                <div className="mt-4 flex items-center justify-center text-gray-500">
                    <LockClosedIcon className="h-5 w-5 mr-2" />
                    <span className="text-sm">Você não tem permissão para visualizar dados salariais</span>
                </div>
            </fieldset>
        );
    }

    // Loading state
    if (isLoading && collaboratorId) {
        return (
            <fieldset data-testid="salary-history-fieldset" className="border p-4 rounded-md">
                <legend className="text-sm font-medium px-1 text-gray-400">Histórico Salarial</legend>
                <div className="mt-4 animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
            </fieldset>
        );
    }

    // Filtrar entradas removidas localmente
    const visibleSavedSalaries = (salaryHistory || [])
        .filter((entry: any) => !locallyRemovedIds.includes(entry.id))
        .map((entry: any) => ({
            ...entry,
            isSaved: true
        }));

    // Combinar dados salvos do banco com dados não salvos do formulário
    const allSalaries = [
        ...visibleSavedSalaries,
        ...unsavedSalaries.map((salary: any, unsavedIndex: number) => ({
            ...salary,
            isSaved: false,
            unsavedIndex
        }))
    ];
    const hasActions = canEditSalary || canRemove;

    const fillFormForEdit = (salary: any) => {
        if (!canEditSalary) return;

        const date = createDateSafe(salary.changeDate || salary.date);
        const dateValue = date ? date.toISOString().slice(0, 10) : '';

        setValue('salaryHistoryEditingId', salary.isSaved ? salary.id || null : null);
        setValue('salaryHistoryEditingIsSaved', Boolean(salary.isSaved));
        setValue('salaryHistoryEditingUnsavedIndex', salary.isSaved ? null : (salary.unsavedIndex ?? null));

        setValue('newSalaryEntryDate', dateValue);
        setValue('newSalaryEntryAmount', salary.amount ?? salary.currentAmount ?? 0);
        setValue('newSalaryEntryExtras', salary.extras ?? salary.currentExtras ?? 0);
        setValue('newSalaryEntrySalaryType', salary.salaryType ?? salary.currentSalaryType ?? 'CLT');
        setValue('newSalaryEntryReason', salary.changeReason ?? salary.reason ?? '');
        setValue('newSalaryEntryObservations', salary.changeObservations ?? salary.observations ?? '');
    };

    return (
        <fieldset data-testid="salary-history-fieldset" className="border p-4 rounded-md">
            <legend className="text-sm font-medium px-1 text-gray-400">
                Histórico Salarial
                {shouldShowAsDisabled && (
                    <span className="ml-2 text-xs text-gray-500">(Somente leitura)</span>
                )}
            </legend>

            {isError && (
                <div className="mt-2 text-sm text-red-600">
                    Erro ao carregar histórico salarial
                </div>
            )}

            <div className="mt-4 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead>
                                <tr>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                                        Data
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Salário
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Extras/Caju
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Tipo
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Motivo
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        Observações
                                    </th>
                                    {hasActions && (
                                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0 text-right">
                                            <span className="sr-only">Ações</span>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {allSalaries.map((salary: any, index: number) => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    const entryDate = createDateSafe(salary.changeDate || salary.date);
                                    const isFuture = entryDate ? entryDate > today : false;
                                    const amount = (salary.amount ?? salary.currentAmount) ?? 0;
                                    const extras = (salary.extras ?? salary.currentExtras) ?? 0;
                                    const salaryType = salary.salaryType ?? salary.currentSalaryType;
                                    const reason = salary.changeReason ?? salary.reason;
                                    const observations = salary.changeObservations ?? salary.observations;

                                    return (
                                        <tr key={`${salary.isSaved ? 'saved' : 'unsaved'}-${salary.id || index}`} data-testid={`salary-history-row-${index}`}>
                                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                                                {formatDateSafe(salary.changeDate || salary.date) || '—'}
                                                {salary.isSaved && (
                                                    <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                                        Salvo
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <span>
                                                    {amount ? new Intl.NumberFormat('pt-BR', {
                                                        style: 'currency',
                                                        currency: 'BRL'
                                                    }).format(amount) : '—'}
                                                </span>
                                                {isFuture && (
                                                    <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                                                        Aumento programado
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {extras ? new Intl.NumberFormat('pt-BR', {
                                                    style: 'currency',
                                                    currency: 'BRL'
                                                }).format(extras) : '—'}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {salaryType || '—'}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {reason || '—'}
                                            </td>
                                            <td className="whitespace-normal px-3 py-4 text-sm text-gray-500 max-w-xs break-words">
                                                {observations || '—'}
                                            </td>
                                            {hasActions && (
                                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {canEditSalary && (
                                                            <button
                                                                type="button"
                                                                onClick={() => fillFormForEdit(salary)}
                                                                className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-400"
                                                                disabled={shouldShowAsDisabled}
                                                                title="Editar registro"
                                                            >
                                                                <PencilSquareIcon className="h-5 w-5" />
                                                            </button>
                                                        )}
                                                        {canRemove && (
                                                            salary.isSaved ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (!salary.id) {
                                                                            return;
                                                                        }
                                                                        handleRemoveSavedSalary(salary.id);
                                                                    }}
                                                                    className="text-red-600 hover:text-red-900 disabled:text-gray-400"
                                                                    disabled={deleteHistory.isPending || !canDeleteHistory}
                                                                    data-testid={`remove-salary-${index}`}
                                                                    title="Remover registro"
                                                                >
                                                                    <TrashIcon className="h-5 w-5" />
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        handleRemoveUnsavedSalary(index);
                                                                    }}
                                                                    className="text-red-600 hover:text-red-900 disabled:text-gray-400"
                                                                    disabled={!canEditSalary}
                                                                    data-testid={`remove-salary-${index}`}
                                                                    title="Remover registro"
                                                                >
                                                                    <TrashIcon className="h-5 w-5" />
                                                                </button>
                                                            )
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                {allSalaries.length === 0 && (
                                    <tr data-testid="no-salary-history">
                                        <td colSpan={hasActions ? 7 : 6} className="text-center py-4 text-sm text-gray-500">
                                            Nenhum histórico de salário registrado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Current salary info if available */}
            {salaryData?.currentEntry && (
                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Salário Atual</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div>
                            <span className="text-blue-700">Valor:</span>
                            <span className="ml-2 font-medium">
                                {new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL'
                                }).format(salaryData.currentEntry.amount)}
                            </span>
                        </div>
                        <div>
                            <span className="text-blue-700">Extras:</span>
                            <span className="ml-2 font-medium">
                                {new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL'
                                }).format(salaryData.currentEntry.extras || 0)}
                            </span>
                        </div>
                        <div>
                            <span className="text-blue-700">Tipo:</span>
                            <span className="ml-2 font-medium">{salaryData.currentEntry.salaryType}</span>
                        </div>
                        <div>
                            <span className="text-blue-700">Vigência:</span>
                            <span className="ml-2 font-medium">{formatDateSafe(salaryData.currentEntry.changeDate)}</span>
                        </div>
                    </div>
                </div>
            )}
            {salaryData?.upcomingEntry && (
                <div className="mt-3 p-3 bg-amber-50 rounded-md border border-amber-200">
                    <h4 className="text-sm font-medium text-amber-900 mb-2">Ajuste Programado</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div>
                            <span className="text-amber-700">Valor:</span>
                            <span className="ml-2 font-medium">
                                {new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL'
                                }).format(salaryData.upcomingEntry.amount)}
                            </span>
                        </div>
                        <div>
                            <span className="text-amber-700">Extras:</span>
                            <span className="ml-2 font-medium">
                                {new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL'
                                }).format(salaryData.upcomingEntry.extras ?? 0)}
                            </span>
                        </div>
                        <div>
                            <span className="text-amber-700">Tipo:</span>
                            <span className="ml-2 font-medium">{salaryData.upcomingEntry.salaryType}</span>
                        </div>
                        <div>
                            <span className="text-amber-700">Vigência:</span>
                            <span className="ml-2 font-medium">{formatDateSafe(salaryData.upcomingEntry.changeDate)}</span>
                        </div>
                    </div>
                </div>
            )}
        </fieldset>
    );
};

export default SalaryHistoryFieldset;
